'use client'

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Grid3x3, Layers, LogIn, UserPlus, User, Menu, X, Grid2x2 } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import { useAuth } from "@/lib/auth-provider"
import { useRouter } from "next/navigation"
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet"
import { LanguageSwitcher } from "@/components/language-switcher"
import { useLanguage } from "@/lib/i18n/language-provider"

export function LandingHeader() {
  const { user } = useAuth()
  const router = useRouter()
  const { t } = useLanguage()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-border/40 bg-background/80 backdrop-blur-lg">
      <div className="container px-4 md:px-6 max-w-7xl mx-auto">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <div className="relative w-8 h-8">
              <Image src="/icon.png" alt="VisualGrid" fill className="object-contain" />
            </div>
            <span className="font-bold text-xl">VisualGrid</span>
          </Link>

          {/* Navigation Desktop */}
          <nav className="hidden md:flex items-center gap-6">
            <Link
              href="/grid"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Grid2x2 className="w-4 h-4" />
              Grid
            </Link>
            <Link
              href="/multigrid"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Layers className="w-4 h-4" />
              MultiGrid
            </Link>
            <Link
              href="/synoptique"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Grid3x3 className="w-4 h-4" />
              {t('landing.header.synoptic')}
            </Link>
          </nav>

          {/* Auth buttons Desktop */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageSwitcher />
            {user ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => router.push('/account')}
              >
                <User className="w-4 h-4 mr-2" />
                {t('landing.header.myAccount')}
              </Button>
            ) : (
              <>
                <Link href="/login">
                  <Button variant="ghost" size="sm">
                    <LogIn className="w-4 h-4 mr-2" />
                    {t('auth.login')}
                  </Button>
                </Link>
                <Link href="/signup">
                  <Button
                    size="sm"
                    variant="outline"
                    className="bg-white dark:bg-white text-black hover:bg-gray-100 dark:hover:bg-gray-100"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    {t('auth.signup')}
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] sm:w-[350px]">
              <div className="flex flex-col gap-6 mt-8">
                <nav className="flex flex-col gap-4">
                  <Link
                    href="/grid"
                    className="flex items-center gap-3 text-base text-muted-foreground hover:text-foreground transition-colors p-3 rounded-lg hover:bg-muted"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Grid2x2 className="w-5 h-5" />
                    Grid
                  </Link>
                  <Link
                    href="/multigrid"
                    className="flex items-center gap-3 text-base text-muted-foreground hover:text-foreground transition-colors p-3 rounded-lg hover:bg-muted"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Layers className="w-5 h-5" />
                    MultiGrid
                  </Link>
                  <Link
                    href="/synoptique"
                    className="flex items-center gap-3 text-base text-muted-foreground hover:text-foreground transition-colors p-3 rounded-lg hover:bg-muted"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Grid3x3 className="w-5 h-5" />
                    {t('landing.header.synoptic')}
                  </Link>
                </nav>

                <div className="border-t border-border pt-6 flex flex-col gap-3">
                  {user ? (
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => {
                        router.push('/account')
                        setMobileMenuOpen(false)
                      }}
                    >
                      <User className="w-5 h-5 mr-3" />
                      {t('landing.header.myAccount')}
                    </Button>
                  ) : (
                    <>
                      <Link href="/login" onClick={() => setMobileMenuOpen(false)}>
                        <Button variant="ghost" className="w-full justify-start">
                          <LogIn className="w-5 h-5 mr-3" />
                          {t('auth.login')}
                        </Button>
                      </Link>
                      <Link href="/signup" onClick={() => setMobileMenuOpen(false)}>
                        <Button
                          variant="outline"
                          className="w-full justify-start bg-white dark:bg-white text-black hover:bg-gray-100 dark:hover:bg-gray-100"
                        >
                          <UserPlus className="w-5 h-5 mr-3" />
                          {t('auth.signup')}
                        </Button>
                      </Link>
                    </>
                  )}
                  <div className="border-t border-border pt-3">
                    <LanguageSwitcher />
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  )
}

